import { Box, Button, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { theme } from '@repo/ui/NerdWalletThemeProvider';
import { startTransition, useCallback, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { ExpandIcon } from '../ExpandIcon';
import { OptionsList } from './OptionsList';

type GenericFilterStoreHook<TFilterOptions extends string> = <T>(
  selector: (state: {
    openedFilters: {
      normalBar: Record<TFilterOptions, boolean>;
      stickyBar: Record<TFilterOptions, boolean>;
    };
    handleOpenedFilters: (
      filter: TFilterOptions,
      bar: 'normalBar' | 'stickyBar',
      state: boolean,
    ) => void;
  }) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => T;

type Props<TFilterOptions extends string> = {
  label: TFilterOptions;
  options: { label: string; count: number }[];
  selectedOptions: string[];
  onChange: (selected: string[]) => void;
  isStickyBar: boolean;
  useStore: GenericFilterStoreHook<TFilterOptions>;
  isFiltersInHeader?: boolean;
};

export const MultiSelectDropdown = <TFilterOptions extends string>({
  label,
  options,
  selectedOptions,
  onChange,
  isStickyBar,
  useStore,
  isFiltersInHeader,
}: Props<TFilterOptions>) => {
  const buttonBorderRadius = isFiltersInHeader ? '8px' : '64px';
  const buttonPaddingY = isFiltersInHeader ? 2 : 1;
  const handleOpenKey = isStickyBar ? 'stickyBar' : 'normalBar';
  const isOpen = useStore(state => state.openedFilters[handleOpenKey][label], shallow);

  const isNormalBarOrStickyOpen = useStore(
    state => state.openedFilters['normalBar'][label] && state.openedFilters['stickyBar'][label],
    shallow,
  );
  const handleOpenedFilters = useStore(state => state.handleOpenedFilters, shallow);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isSelectedAndExperimentOn = !!selectedOptions.length;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        startTransition(() => {
          handleOpenedFilters(label, handleOpenKey, false);
        });
        buttonRef.current?.focus();
      }
    },
    [handleOpenedFilters, handleOpenKey],
  );

  const updateSelection = useCallback(
    (option: string) => {
      const isSelected = selectedOptions.includes(option);
      const newSelected = isSelected
        ? selectedOptions.filter(item => item !== option)
        : [...selectedOptions, option];

      onChange(newSelected);
    },
    [selectedOptions],
  );

  const handleToggleOpen = useCallback(() => {
    startTransition(() => {
      handleOpenedFilters(label, handleOpenKey, !isOpen);
    });
  }, [handleOpenedFilters, handleOpenKey, isOpen]);

  const selectedOptionsSet = useMemo(() => new Set(selectedOptions), [selectedOptions]);
  return (
    <Box ref={dropdownRef}>
      <Button
        ref={buttonRef}
        onClick={handleToggleOpen}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${label}-dropdown-options`}
        variant="outlined"
        fullWidth
        sx={{
          justifyContent: 'space-between',
          textAlign: 'left',
          backgroundColor: isSelectedAndExperimentOn ? 'greens.light' : 'white',
          borderWidth: isSelectedAndExperimentOn ? '2px' : '1px',
          borderColor: isSelectedAndExperimentOn ? 'primary.main' : 'neutral.lighter',
          borderRadius: buttonBorderRadius,
          py: buttonPaddingY,
          minHeight: '40px',
          userSelect: 'none',
          textTransform: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'blues.light',
            cursor: 'pointer',
          },
          transition: 'none',
        }}
      >
        <Typography
          variant="textBold"
          sx={{
            fontSize: '14px',
            color: isSelectedAndExperimentOn ? 'primary.main' : 'neutral.dark',
          }}
        >
          {`${label} ${isSelectedAndExperimentOn ? '+' + selectedOptions.length : ''}`}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'neutral.dark',
            transform: isNormalBarOrStickyOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: theme.transitions.create('transform', {
              duration: theme.transitions.duration.short,
            }),
            ml: 2,
          }}
        >
          <ExpandIcon
            width={15}
            height={15}
            color={
              isSelectedAndExperimentOn ? theme.palette.primary.main : theme.palette.neutral.dark
            }
          />
        </Box>
      </Button>

      <Popper
        open={isOpen}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        popperOptions={{ strategy: 'fixed' }}
        sx={{ zIndex: 200 }}
      >
        <ClickAwayListener onClickAway={() => handleOpenedFilters(label, handleOpenKey, false)}>
          <Paper
            id="dropdown-options"
            role="listbox"
            aria-label={label}
            aria-multiselectable="true"
            tabIndex={-1}
            elevation={4}
            sx={{
              minWidth: '225px',
              borderRadius: '8px',
              py: 2,
              my: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 2,
                px: 3,
              }}
            >
              <Typography variant="textBold" sx={{ fontSize: '14px', color: 'neutral.dark' }}>
                {label}
              </Typography>
            </Box>

            <OptionsList
              options={options}
              selectedOptionsSet={selectedOptionsSet}
              onOptionSelect={updateSelection}
              onClose={() => {
                handleOpenedFilters(label, handleOpenKey, false);
                buttonRef.current?.focus();
              }}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
